import React, { useEffect, useMemo, useState } from "react";
import { CaretDown, Folder, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";

import { documents as documentsApi, folders as foldersApi } from "@/api";
import * as mailAttachmentsApi from "@/api/mailAttachments";
import ActaModal from "@/components/ui/ActaModal";
import { Button } from "@/components/ui/button";


function buildFolderTree(folders, parentId = null) {
  return folders
    .filter((folder) => (folder.parentId || null) === parentId)
    .sort((a, b) => (a.name || "").localeCompare(b.name || "", "fr"))
    .map((folder) => ({
      ...folder,
      children: buildFolderTree(folders, folder.id),
    }));
}


function FolderChoice({
  folder,
  level,
  selectedFolderId,
  expandedFolderIds,
  onToggle,
  onSelect,
}) {
  const hasChildren = folder.children.length > 0;
  const isExpanded = expandedFolderIds.has(folder.id);
  const isSelected = selectedFolderId === folder.id;
  const canEdit = Boolean(folder.permissions?.canEdit);

  return (
    <div>
      <div
        className={[
          "flex min-h-[44px] items-center gap-2 rounded-lg px-2 py-1.5",
          isSelected
            ? "bg-[var(--do-accent-soft)] text-[var(--do-accent)]"
            : "text-[var(--do-ink)]",
          canEdit ? "" : "opacity-45",
        ].join(" ")}
        style={{ paddingLeft: `${8 + level * 16}px` }}
      >
        <Folder size={18} weight="duotone" className="shrink-0" />

        <button
          type="button"
          onClick={() => canEdit && onSelect(folder.id)}
          disabled={!canEdit}
          className="min-w-0 flex-1 truncate text-left text-sm font-bold"
          title={canEdit ? folder.name : `${folder.name} — lecture seule`}
        >
          {folder.name}
        </button>

        {hasChildren && (
          <button
            type="button"
            onClick={() => onToggle(folder.id)}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md hover:bg-[var(--do-bg-2)]"
            aria-label={isExpanded ? "Réduire le dossier" : "Développer le dossier"}
            aria-expanded={isExpanded}
          >
            <CaretDown
              size={14}
              className={isExpanded ? "" : "-rotate-90"}
            />
          </button>
        )}
      </div>

      {hasChildren && isExpanded && (
        <div>
          {folder.children.map((child) => (
            <FolderChoice
              key={child.id}
              folder={child}
              level={level + 1}
              selectedFolderId={selectedFolderId}
              expandedFolderIds={expandedFolderIds}
              onToggle={onToggle}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
}


export default function MailAttachmentSaveDialog({
  open,
  messageId,
  attachment,
  onClose,
}) {
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState("");
  const [expandedFolderIds, setExpandedFolderIds] = useState(new Set());

  useEffect(() => {
    if (!open) {
      setFolders([]);
      setSelectedFolderId("");
      setExpandedFolderIds(new Set());
      return;
    }

    let cancelled = false;
    setLoading(true);

    foldersApi
      .list()
      .then((items) => {
        if (cancelled) return;
        const nextFolders = Array.isArray(items) ? items : [];
        setFolders(nextFolders);
        setExpandedFolderIds(
          new Set(
            nextFolders
              .filter((folder) => folder.parentId == null)
              .map((folder) => folder.id)
          )
        );
      })
      .catch((error) => {
        if (!cancelled) {
          toast.error(
            error?.response?.data?.detail
            || "Impossible de charger les dossiers Documents"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open]);

  const folderTree = useMemo(
    () => buildFolderTree(folders),
    [folders]
  );

  const hasWritableFolder = useMemo(
    () => folders.some((folder) => folder.permissions?.canEdit),
    [folders]
  );

  function toggleFolder(folderId) {
    setExpandedFolderIds((current) => {
      const next = new Set(current);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      return next;
    });
  }

  async function saveAttachment() {
    if (!messageId || !attachment?.id || !selectedFolderId) return;

    setSaving(true);

    try {
      const blob = await mailAttachmentsApi.getFile(
        messageId,
        attachment.id
      );

      const file = new window.File(
        [blob],
        attachment.filename || "piece-jointe",
        {
          type:
            blob?.type
            || attachment.contentType
            || "application/octet-stream",
        }
      );

      await documentsApi.upload(file, selectedFolderId);

      toast.success("Pièce jointe enregistrée dans Documents");
      onClose();
    } catch (error) {
      toast.error(
        error?.response?.data?.detail
        || "Impossible d’enregistrer la pièce jointe dans Documents"
      );
    } finally {
      setSaving(false);
    }
  }

  if (!open) return null;

  return (
    <ActaModal
      open
      onClose={saving ? undefined : onClose}
      title="Enregistrer dans Documents"
    >
      <div
        className="min-w-0 space-y-4 [overflow-wrap:anywhere]"
        data-testid="mail-save-attachment-dialog"
      >
        <div className="rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-2)] p-3">
          <div className="text-xs font-black uppercase tracking-wide text-[var(--do-muted)]">
            Pièce jointe
          </div>
          <div className="mt-1 truncate text-sm font-bold text-[var(--do-ink)]">
            {attachment?.filename || "Pièce jointe"}
          </div>
        </div>

        <div>
          <div className="mb-2 text-xs font-black uppercase tracking-wide text-[var(--do-muted)]">
            Choisir le dossier de destination
          </div>

          <div className="max-h-[52vh] overflow-y-auto rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-2">
            {loading ? (
              <div className="p-4 text-sm text-[var(--do-muted)]">
                Chargement des dossiers…
              </div>
            ) : folderTree.length === 0 ? (
              <div className="p-4 text-sm text-[var(--do-muted)]">
                Aucun dossier Documents disponible.
              </div>
            ) : (
              folderTree.map((folder) => (
                <FolderChoice
                  key={folder.id}
                  folder={folder}
                  level={0}
                  selectedFolderId={selectedFolderId}
                  expandedFolderIds={expandedFolderIds}
                  onToggle={toggleFolder}
                  onSelect={setSelectedFolderId}
                />
              ))
            )}
          </div>

          {!loading && folderTree.length > 0 && !hasWritableFolder && (
            <p className="mt-2 text-xs text-[var(--do-muted)]">
              Vous n’avez aucun dossier Documents modifiable.
            </p>
          )}
        </div>

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="actaSecondary"
            onClick={onClose}
            disabled={saving}
          >
            Annuler
          </Button>
          <Button
            type="button"
            variant="actaPrimary"
            onClick={saveAttachment}
            disabled={!selectedFolderId || saving}
            className="gap-2"
          >
            <FloppyDisk size={17} />
            {saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </div>
      </div>
    </ActaModal>
  );
}
